<template>
  <div class="table-wrapper">
    <el-row
      class="table-wrapper__top-actions"
      :gutter="20"
      type="flex"
    >
      <el-col :span="22">
        <!-- Bulk Disable -->
        <div
          class="massive-action-disable"
          v-if="enableSelection && bulkDisable"
        >
          <div>{{ $t('tableButtons.disableSelected') }}</div>
          <el-button
            class="disable-selected"
            type="primary"
            @click="$emit('bulkDisable', multipleSelection)"
          >
            {{ $t('global.apply') }}
          </el-button>
        </div>
        <!-- End Bulk Disable -->
        <!-- Single Bulk Action -->
        <div
          class="massive-action-disable"
          v-if="enableSelection && customBulkAction"
        >
          <el-button
            class="disable-selected"
            type="primary"
            :disabled="multipleSelection.length === 0"
            @click="customBulkActionOptions.action(multipleSelection)"
          >
            {{ customBulkActionOptions.label }}
          </el-button>
        </div>
        <!-- End Single Bulk Action -->
        <!-- Multiple Bulk Actions -->
        <div
          class="massive-actions"
          v-if="enableSelection && massiveActionsOptions.length"
        >
          <label class="massive-actions__label">
            {{ $t('table.massiveActions.title') }}
          </label>
          <div class="massive-actions__select">
            <el-select
              v-model="massiveActionsValue"
              :disabled="!multipleSelection.length"
              :placeholder="$t('global.select')"
              size="mini"
            >
              <el-option
                v-for="item in massiveActionsOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
                :disabled="item.disabled"
              />
            </el-select>
            <el-button
              :disabled="
                massiveActionsValue == null ||
                massiveActionsValue === '' ||
                !multipleSelection.length
              "
              type="primary"
              size="mini"
              @click="
                $emit('massiveAction', massiveActionsValue, multipleSelection)
              "
            >
              {{ $t('table.massiveActions.submit') }}
            </el-button>
          </div>
        </div>
        <!-- End Multiple Bulk Actions -->
      </el-col>
      <el-col
        :span="2"
        class="table-additional-actions"
      >
        <div v-if="configurableColumns">
          <el-button
            type="default"
            @click="showConfigOptions = !showConfigOptions"
          >
            <i class="fas fa-table"></i>
          </el-button>
        </div>
      </el-col>
    </el-row>

    <TableColumnsConfig
      v-if="configurableColumns && showConfigOptions"
      :table-key="tableKey"
      :available-columns="columns"
      @close="showConfigOptions = false"
    />

    <div class="table-content">
      <div
        v-if="title"
        class="table-title"
      >
        {{ title }}
      </div>
      <el-table
        ref="multipleTable"
        :key="stickyColumnsKey"
        :row-key="rowKey"
        :default-sort="sorting"
        :data="tableRows"
        :height="height"
        :cell-class-name="tableCellClassName"
        :row-class-name="customRowClass"
        :lazy="lazy"
        :tree-props="treeProps"
        :load="load"
        border
        @selection-change="handleSelectionChange"
        @sort-change="debouncedSort"
      >
        <slot name="beforeColumns" />
        <el-table-column
          v-if="enableSelection"
          type="selection"
        />
        <el-table-column
          v-for="(column, index) in filteredColumns"
          :key="index"
          :prop="index"
          :label="$t(`filters.${column.label}`)"
          :sortable="column.sortable"
          :align="column.align"
          :min-width="column.minWidth ? column.minWidth : ''"
          :fixed="columnFixed(column)"
        >
          <template slot-scope="scope">
            <span v-if="column.type === 'text' && !column.ellipsis">{{
              propertyValue(scope.row, column.key)
            }}</span>
            <el-tooltip
              v-if="column.type === 'text' && column.ellipsis"
              placement="top"
              popper-class="cell-ellipsis-tooltip"
              :disabled="!propertyValue(scope.row, column.key)"
            >
              <template slot="content">
                {{ propertyValue(scope.row, column.key) }}
              </template>
              <span class="cell-ellipsis">
                {{ propertyValue(scope.row, column.key) }}
              </span>
            </el-tooltip>
            <span
              v-if="column.type === 'text-with-subtitle'"
              class="cell-with-subtitle"
            >
              <span>{{ propertyValue(scope.row, column.key) }}</span>
              <span
                v-if="propertyValue(scope.row, column.subtitleKey)"
                class="cell-with-subtitle__subtitle"
              >
                ({{ propertyValue(scope.row, column.subtitleKey) }})
              </span>
            </span>
            <span v-if="column.type === 'link'">
              <router-link
                v-if="propertyValue(scope.row, column.key)"
                :to="column.to(scope.row)"
              >
                {{ propertyValue(scope.row, column.key) }}
              </router-link>
            </span>
            <span v-if="column.type === 'tag'">
              <el-tag
                v-if="propertyValue(scope.row, column.key)"
                :type="column.tagType ? column.tagType(scope.row) : ''"
                size="mini"
              >
                <template v-if="column.translated">
                  {{ $t(column.getLabel(scope.row)) }}
                </template>
                <template v-else>
                  {{
                    column.getLabel
                      ? column.getLabel(scope.row)
                      : propertyValue(scope.row, column.key)
                  }}
                </template>
              </el-tag>
            </span>
            <span v-if="column.type === 'translatedText'">
              {{ $t(`filters.${propertyValue(scope.row, column.key)}`) }}
            </span>
            <span v-if="column.type === 'boolean'">
              <span
                :style="{ cursor: column.emit ? 'pointer' : 'auto' }"
                @click="
                  () => {
                    if (column.emit) $emit(column.emit, scope.row);
                  }
                "
              >
                <i
                  v-if="scope.row[index]"
                  class="state-icon fas fa-check-circle"
                />
                <i
                  v-else
                  class="state-icon fas fa-do-not-enter"
                />
              </span>
            </span>
            <span v-if="column.type === 'date'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{ propertyValue(scope.row, column.key) | date('DD/MM/YYYY') }}
              </span>
            </span>
            <span v-if="column.type === 'datetime'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  propertyValue(scope.row, column.key)
                    | datetime('DD/MM/YYYY HH:mm')
                }}
              </span>
            </span>
            <span v-if="column.type === 'datetimeseconds'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  propertyValue(scope.row, column.key)
                    | datetime('DD/MM/YYYY HH:mm:ss')
                }}
              </span>
            </span>
            <span v-if="column.type === 'enum'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  $t(
                    `enums.${column.enum}.${propertyValue(
                      scope.row,
                      column.key
                    )}`
                  )
                }}
              </span>
            </span>
            <span v-if="column.type === 'encoded'">
              <span v-if="propertyValue(scope.row, column.key) !== ''">
                {{
                  $t(
                    `enums.${column.enum}.${column.decode(
                      propertyValue(scope.row, column.key)
                    )}`
                  )
                }}
              </span>
            </span>
            <span v-if="column.type === 'function'">
              {{ column.get(propertyValue(scope.row, column.key), scope.row) }}
            </span>
            <span v-if="column.type === 'function-i18n'">
              {{
                $t(column.get(propertyValue(scope.row, column.key), scope.row))
              }}
            </span>
            <span
              v-if="column.type === 'slot-collapse-list'"
              class="slot-collapse-list"
            >
              <el-collapse accordion>
                <el-collapse-item
                  :title="$t(column.collapseTitle)"
                  name="slot-collapse-item"
                >
                  <a
                    v-for="(el, collapseIndex) in scope.row[column.collapseKey]"
                    :key="`${column.collapseKey}-${collapseIndex}`"
                    href="#"
                    @click.prevent="$emit('collapseItemClick', scope.row, el)"
                  >
                    <i
                      :class="`state-icon fas fa-${column.collapseItemIcon}`"
                    />
                    {{ el[column.collapseValue] }}
                  </a>
                </el-collapse-item>
              </el-collapse>
            </span>
            <TableActions
              v-else-if="column.type === 'actions'"
              :has-permissions="hasPermissions"
              :configuration="column"
              :row="scope.row"
              @actionClick="$emit($event, scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <el-row
      :gutter="20"
      type="flex"
      align="bottom"
      class="pagination-content"
    >
      <el-col
        v-if="showRefreshButton"
        :span="24"
      >
        <el-button
          type="primary"
          :icon="refreshButtonIcon"
          @click="$emit('refreshTable')"
        />
      </el-col>
      <el-col
        v-if="showPagination"
        :span="24"
      >
        <div class="pagination-container">
          <el-pagination
            class="pagination"
            :current-page="currentPage"
            :page-sizes="[1, 5, 10, 50, 100]"
            :page-size="perPage"
            layout="sizes, prev, pager, next"
            :total="count"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import lodashGet from 'lodash.get';
  import debounce from 'lodash.debounce';
  import TableActions from '@/components/TableActions.vue';

  import acl from '../services/acl';
  import TableColumnsConfig from './TableColumnsConfig.vue';

  export default {
    name: 'TableWrapper',
    components: {
      TableActions,
      TableColumnsConfig,
    },
    props: {
      tableColumns: {
        type: Object,
        default: () => {},
      },
      tableKey: {
        type: String,
        required: true,
      },
      enableSelection: {
        type: Boolean,
        default: false,
      },
      bulkDisable: {
        type: Boolean,
        default: false,
      },
      customBulkAction: {
        type: Boolean,
        deafult: false,
      },
      customBulkActionOptions: {
        type: Object,
        default: () => {},
      },
      hasPermissions: {
        type: Boolean,
        default: true,
      },
      columnCondition: {
        type: String,
        default: '',
      },
      massiveActionsOptions: {
        type: Array,
        default: () => [],
      },
      height: {
        type: String,
        default: undefined,
      },
      rowKey: {
        type: String,
        default: undefined,
      },
      treeProps: {
        type: Object,
        default: undefined,
      },
      load: {
        type: Function,
        default: undefined,
      },
      lazy: {
        type: Boolean,
        default: false,
      },
      showPagination: { type: Boolean, default: true },
      showRefreshButton: { type: Boolean, default: false },
      configurableColumns: { type: Boolean, deafult: false },
      visibleColumns: { type: Array, default: () => [] },
      customRowClass: { type: Function, default: () => '' },
      title: { type: String, default: '' },
    },
    data() {
      return {
        multipleSelection: [],
        massiveActionsValue: '',
        showConfigOptions: false,
        refreshButtonIcon: 'fas fa-sync-alt',
        debouncedSort: null,
        horizontalScrollTarget: null,
        wheelPanTarget: null,
      };
    },
    computed: {
      ...mapGetters(['tablesStatus', 'enabledColumns', 'stickyColumns']),
      stickyColumnsKey() {
        return this.stickyColumns(this.tableKey).join('|');
      },
      sorting() {
        return this.tablesStatus[this.tableKey].sorting;
      },
      currentPage() {
        return this.tablesStatus[this.tableKey].page;
      },
      perPage() {
        return this.tablesStatus[this.tableKey].perPage;
      },
      enablePagination() {
        return this.tablesStatus[this.tableKey].enablePagination;
      },
      count() {
        return this.tablesStatus[this.tableKey].count;
      },
      tableRows() {
        return this.tablesStatus[this.tableKey].rows;
      },
      columns() {
        return Object.keys(this.tableColumns).reduce((columns, key) => {
          if (
            acl.userCanUse(this.tableColumns[key].permissions) &&
            this.checkColumnCondition(this.tableColumns[key])
          ) {
            return { ...columns, [key]: this.tableColumns[key] };
          }
          return columns;
        }, {});
      },
      filteredColumns() {
        const enabledColumns = this.enabledColumns(this.tableKey);
        if (enabledColumns.length) {
          return Object.keys(this.columns).reduce((columns, key) => {
            if (
              enabledColumns.includes(this.columns[key].label) ||
              this.columns[key].type === 'actions'
            ) {
              return { ...columns, [key]: this.columns[key] };
            }
            return columns;
          }, {});
        }
        return this.columns;
      },
    },
    created() {
      this.debouncedSort = debounce(this.sortChange, 1000);
    },
    mounted() {
      this.$emit('reload');
      this.bindWheelPan();
    },
    beforeDestroy() {
      this.unbindWheelPan();
    },
    watch: {
      stickyColumnsKey() {
        this.unbindWheelPan();
        // the remounted table starts unchecked without emitting selection-change
        this.handleSelectionChange([]);
        this.massiveActionsValue = '';
        this.$nextTick(this.bindWheelPan);
      },
    },
    methods: {
      ...mapActions([
        // 'updateTableSorting',
        // 'updateTablePerPage',
        // 'updateTableCurrentPage',
        // 'updateColumnSort',
      ]),
      columnFixed(column) {
        if (this.stickyColumns(this.tableKey).includes(column.label)) {
          return 'left';
        }
        return column.fixed ? 'right' : false;
      },
      checkColumnCondition(column) {
        return (
          !column.columnCondition ||
          (column.columnCondition &&
            column.columnCondition === this.columnCondition)
        );
      },
      tableCellClassName({ row, column }) {
        if (
          this.columns[column.property] &&
          this.columns[column.property].customClass
        ) {
          const modifier =
            this.columns[column.property].getClassModifier &&
            `table-cell-${this.columns[column.property].key}--${this.columns[
              column.property
            ].getClassModifier(row[column.property], row)}`;
          return `table-cell-${this.columns[column.property].key} ${
            modifier || ''
          }`;
        }
        return '';
      },
      handleSelectionChange(val) {
        this.multipleSelection = val;
        this.$emit('selectionChange', val);
      },
      bindWheelPan() {
        const table = this.$refs.multipleTable;
        if (!table) return;
        this.wheelPanTarget = table.$el;
        this.horizontalScrollTarget = table.$el.querySelector(
          '.el-table__body-wrapper'
        );
        this.wheelPanTarget.addEventListener('wheel', this.onTableWheel, {
          passive: false,
        });
      },
      unbindWheelPan() {
        if (this.wheelPanTarget) {
          this.wheelPanTarget.removeEventListener('wheel', this.onTableWheel);
        }
        this.wheelPanTarget = null;
        this.horizontalScrollTarget = null;
      },
      onTableWheel(event) {
        const target = this.horizontalScrollTarget;
        if (!target || event.deltaX !== 0) return;
        if (target.scrollWidth <= target.clientWidth) return;
        // A table with its own vertical scroll (the `height` prop) still
        // needs the wheel to scroll rows first.
        if (target.scrollHeight > target.clientHeight) return;
        const max = target.scrollWidth - target.clientWidth;
        // Already at an edge in the wheel's direction: let the page scroll
        // instead of trapping the wheel on a table that can't pan further.
        if (event.deltaY > 0 && target.scrollLeft >= max) return;
        if (event.deltaY < 0 && target.scrollLeft <= 0) return;
        target.scrollLeft += event.deltaY;
        event.preventDefault();
      },
      sortChange(sorting) {
        const { column, ...sortData } = sorting;
        let propKey = this.columns[sortData.prop].key;
        if (propKey.includes('location.name')) {
          propKey = 'locationId';
        } else if (propKey.includes('.')) {
          const propKeyList = this.columns[sortData.prop].key.split('.');
          propKey = propKeyList[propKeyList.length - 1];
        }
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              sorting: JSON.stringify({ ...sortData, prop: propKey }),
            },
          })
          .catch(() => {});
      },
      handleSizeChange(perPage) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              perPage,
              page: 1,
            },
          })
          .catch(() => {});
      },
      handleCurrentChange(page) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              page,
            },
          })
          .catch(() => {});
      },
      handlePaginationToggle(status) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              page: 1,
              pagination: `${status}`,
            },
          })
          .catch(() => {});
      },
      propertyValue(object, path) {
        return lodashGet(object, path, '');
      },
    },
  };
</script>

<style lang="scss">
  .table-wrapper {
    .table-content {
      .table-title {
        font-size: $--font-size-base;
        font-weight: 700;
        color: #a2a4ae;
        margin-bottom: 10px;
      }
    }
  }

  .cell-ellipsis {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    // color: #000000;
  }

  .el-tooltip__popper.cell-ellipsis-tooltip {
    color: #000000;
  }

  // Two-line cell: the main value on top, a secondary one underneath (e.g. the parent
  // analysis code with its description). The row height grows to fit both lines.
  .cell-with-subtitle {
    display: flex;
    flex-direction: column;
    line-height: 1.3;

    &__subtitle {
      margin-top: 5px;
      font-size: 0.75em;
      line-height: 1.2;
      color: #a2a4ae;
      font-style: italic;
    }
  }
</style>
